// src/pages/user/UserSettings.tsx
// Route: /user/settings
//
// Four sections in a single scrollable page:
//   1. Profile — name, avatar (display only — editable)
//   2. Change password
//   3. Children summary (read-only)
//   4. Notification preferences (local toggle — future backend hook)

import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectUser, updateUser } from "../../app/features/authSlice";
import {
  useGetProfileQuery,
  useUpdateProfileMutation,
} from "../../app/api/parentApi";
import { useChangePasswordMutation } from "../../app/api/authApi";
import useHashScroll from "../../app/useHashScroll";
import type { ParentProfile } from "../../app/api/parentApi";

import { Check } from "lucide-react";
/* ─── atoms ─────────────────────────────────────────────────────── */

function Skeleton({ className = "" }: { className?: string }) {
  return (
    <div className={`animate-pulse rounded-xl bg-[#ebeff7] ${className}`} />
  );
}

function SectionCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-[22px] border border-[#e5e8ef] bg-white p-6 space-y-5">
      <div>
        <h2 className="text-base font-bold text-[#16213e]">{title}</h2>
        {subtitle && (
          <p className="mt-0.5 text-xs text-[#8189a0]">{subtitle}</p>
        )}
      </div>
      {children}
    </div>
  );
}

const inputCls =
  "w-full rounded-xl border border-[#dde2ec] px-3 py-2.5 text-sm outline-none focus:border-[#1b2d5e] focus:ring-1 focus:ring-[#1b2d5e]/20 transition";
const readonlyCls =
  "w-full rounded-xl border border-[#ebeff7] bg-[#f9fafc] px-3 py-2.5 text-sm text-[#5a6278] cursor-default";

const TRACK_LABELS: Record<string, string> = {
  foundation_builders: "Foundation Builders (6–9)",
  explorers: "Explorers (9–12)",
  innovators: "Innovators (13–18)",
};

/* ─── section 1: profile ─────────────────────────────────────────── */

function ProfileSection({ profile }: { profile: ParentProfile }) {
  const dispatch = useDispatch();
  const authUser = useSelector(selectUser);
  const [update, { isLoading }] = useUpdateProfileMutation();

  const [firstName, setFirstName] = useState(profile.firstName);
  const [lastName, setLastName] = useState(profile.lastName);
  const [dirty, setDirty] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setDirty(firstName !== profile.firstName || lastName !== profile.lastName);
    setSuccess("");
    setError("");
  }, [firstName, lastName]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      setError("Name fields are required");
      return;
    }
    setError("");
    setSuccess("");
    try {
      await update({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      }).unwrap();
      // Update the auth slice so the topbar reflects the new name immediately
      if (authUser) {
        dispatch(
          updateUser({
            ...authUser,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
          }),
        );
      }
      setSuccess("Profile updated successfully.");
      setDirty(false);
    } catch (e: any) {
      setError(e?.data?.error ?? "Failed to update profile.");
    }
  };

  const initials =
    `${profile.firstName.charAt(0)}${profile.lastName.charAt(0)}`.toUpperCase();

  return (
    <SectionCard
      title="Your profile"
      subtitle="Update your name. Your email cannot be changed here."
    >
      {/* Avatar */}
      <div className="flex items-center gap-4">
        <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-[16px] bg-[#1b2d5e] text-lg font-bold text-white shadow-[0_4px_16px_rgba(27,45,94,0.25)]">
          {initials}
        </span>
        <div>
          <p className="text-sm font-semibold text-[#16213e]">
            {profile.firstName} {profile.lastName}
          </p>
          <p className="text-xs text-[#8189a0]">{profile.email}</p>
          <span className="mt-1 inline-flex items-center rounded-full bg-[#fff4f1] border border-[#f5c5b0] px-2.5 py-0.5 text-[11.5px] font-bold text-[#7a3020]">
            Parent
          </span>
        </div>
      </div>

      {/* Editable fields */}
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1.5 block text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            First name
          </label>
          <input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className={inputCls}
          />
        </div>
        <div>
          <label className="mb-1.5 block text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Last name
          </label>
          <input
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            className={inputCls}
          />
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1.5 block text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Email address
          </label>
          <input value={profile.email} readOnly className={readonlyCls} />
          <p className="mt-1 text-[11.5px] text-[#a6adbf]">
            Contact the academy to change your email address.
          </p>
        </div>
        <div>
          <label className="mb-1.5 block text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Account type
          </label>
          <input value="Parent" readOnly className={readonlyCls} />
        </div>
        <div>
          <label className="mb-1.5 block text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Email verified
          </label>
          <input
            value={profile.isEmailVerified ? "Verified" : "Not verified"}
            readOnly
            className={readonlyCls}
          />
        </div>
      </div>

      {error && (
        <p className="rounded-xl border border-[#f5c0c0] bg-[#fff3f3] px-3 py-2 text-xs font-semibold text-[#9b2a2a]">
          {error}
        </p>
      )}
      {success && (
        <p className="rounded-xl border border-[#b6e8d0] bg-[#edfaf4] px-3 py-2 text-xs font-semibold text-[#2a7a56]">
          <Check className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}{success}
        </p>
      )}

      {dirty && (
        <button
          onClick={handleSave}
          disabled={isLoading}
          className="w-full rounded-xl bg-[#1b2d5e] py-2.5 text-sm font-bold text-white shadow-[0_4px_16px_rgba(27,45,94,0.30)] transition hover:bg-[#24397a] disabled:opacity-60"
        >
          {isLoading ? "Saving…" : "Save changes"}
        </button>
      )}
    </SectionCard>
  );
}

/* ─── section 2: change password ─────────────────────────────────── */

function ChangePasswordSection() {
  const [changePassword, { isLoading }] = useChangePasswordMutation();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleSubmit = async () => {
    setError("");
    setSuccess("");
    if (!current) {
      setError("Current password is required");
      return;
    }
    if (next.length < 8) {
      setError("New password must be at least 8 characters");
      return;
    }
    if (next !== confirm) {
      setError("New passwords do not match");
      return;
    }
    try {
      await changePassword({
        currentPassword: current,
        newPassword: next,
      }).unwrap();
      setSuccess("Password changed successfully. Please keep it safe.");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (e: any) {
      setError(
        e?.data?.error ??
          "Failed to change password. Check your current password and try again.",
      );
    }
  };

  const type = showPwd ? "text" : "password";

  return (
    <SectionCard
      title="Change password"
      subtitle="Choose a strong password — at least 8 characters."
    >
      <div className="space-y-3">
        <div>
          <label className="mb-1.5 block text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Current password
          </label>
          <div className="relative">
            <input
              type={type}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              placeholder="Your current password"
              className={inputCls + " pr-14"}
            />
            <button
              type="button"
              onClick={() => setShowPwd((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[11.5px] font-bold text-[#8189a0] hover:text-[#3b4560] transition"
            >
              {showPwd ? "HIDE" : "SHOW"}
            </button>
          </div>
        </div>
        <div>
          <label className="mb-1.5 block text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            New password
          </label>
          <input
            type={type}
            value={next}
            onChange={(e) => setNext(e.target.value)}
            placeholder="At least 8 characters"
            className={inputCls}
          />
          {/* Strength indicator */}
          {next.length > 0 && (
            <div className="mt-1.5 flex items-center gap-1.5">
              {[
                next.length >= 8,
                /[A-Z]/.test(next),
                /[0-9]/.test(next),
                /[^A-Za-z0-9]/.test(next),
              ].map((ok, i) => (
                <div
                  key={i}
                  className={`h-1 flex-1 rounded-full transition-colors ${ok ? "bg-[#6cc2a0]" : "bg-[#ebeff7]"}`}
                />
              ))}
              <span className="ml-1 text-[11.5px] font-semibold text-[#8189a0]">
                {[
                  next.length >= 8,
                  /[A-Z]/.test(next),
                  /[0-9]/.test(next),
                  /[^A-Za-z0-9]/.test(next),
                ].filter(Boolean).length === 4
                  ? "Strong"
                  : next.length >= 8
                    ? "Fair"
                    : "Weak"}
              </span>
            </div>
          )}
        </div>
        <div>
          <label className="mb-1.5 block text-[12.5px] font-bold uppercase tracking-wider text-[#8189a0]">
            Confirm new password
          </label>
          <input
            type={type}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Same as above"
            className={inputCls}
          />
        </div>
      </div>

      {error && (
        <p className="rounded-xl border border-[#f5c0c0] bg-[#fff3f3] px-3 py-2 text-xs font-semibold text-[#9b2a2a]">
          {error}
        </p>
      )}
      {success && (
        <p className="rounded-xl border border-[#b6e8d0] bg-[#edfaf4] px-3 py-2 text-xs font-semibold text-[#2a7a56]">
          <Check className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden />{" "}{success}
        </p>
      )}

      <button
        onClick={handleSubmit}
        disabled={isLoading}
        className="w-full rounded-xl bg-[#16213e] py-2.5 text-sm font-bold text-white transition hover:bg-[#3d3f55] disabled:opacity-60"
      >
        {isLoading ? "Changing…" : "Change password"}
      </button>
    </SectionCard>
  );
}

/* ─── section 3: children summary ───────────────────────────────── */

function ChildrenSection({ profile }: { profile: ParentProfile }) {
  if (profile.children.length === 0) return null;

  const STATUS_LABELS: Record<string, { label: string; cls: string }> = {
    enrolled: {
      label: "Enrolled",
      cls: "bg-[#edfaf4] text-[#2a7a56] border-[#b6e8d0]",
    },
    pending: {
      label: "Pending",
      cls: "bg-[#fff8ee] text-[#7a4a10] border-[#f5d9a0]",
    },
    waitlisted: {
      label: "Waitlisted",
      cls: "bg-[#ebeff7] text-[#4a44b0] border-[#cdd4e2]",
    },
    inactive: {
      label: "Inactive",
      cls: "bg-[#f1f3f8] text-[#8189a0] border-[#dde2ec]",
    },
    graduated: {
      label: "Graduated",
      cls: "bg-[#edfaf4] text-[#1a5a36] border-[#6cc2a0]",
    },
  };

  return (
    <SectionCard
      title="Your children"
      subtitle="Children enrolled under your account. Manage enrolments from the Enrolments tab."
    >
      <div className="space-y-2">
        {profile.children.map((c: ParentProfile["children"][number]) => {
          const st = STATUS_LABELS[c.studentStatus] ?? {
            label: c.studentStatus,
            cls: "bg-[#f1f3f8] text-[#8189a0] border-[#dde2ec]",
          };
          return (
            <div
              key={c.id}
              className="flex items-center gap-3 rounded-[16px] border border-[#e5e8ef] bg-[#f9fafc] px-4 py-3"
            >
              <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-[#ebeff7] text-[12.5px] font-bold text-[#1b2d5e]">
                {c.firstName.charAt(0)}
                {c.lastName.charAt(0)}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-[#16213e]">
                  {c.firstName} {c.lastName}
                </p>
                <p className="text-[12.5px] text-[#8189a0]">
                  Age {c.age} · {TRACK_LABELS[c.track] ?? c.track}
                </p>
              </div>
              <span
                className={`flex-shrink-0 rounded-full border px-2.5 py-0.5 text-[11.5px] font-bold ${st.cls}`}
              >
                {st.label}
              </span>
            </div>
          );
        })}
      </div>
    </SectionCard>
  );
}

/* ─── section 4: notification preferences ───────────────────────── */

function NotificationSection() {
  // Stored in localStorage — extend with backend when ready
  const load = () => {
    try {
      return JSON.parse(localStorage.getItem("fsl_notif_prefs") ?? "{}");
    } catch {
      return {};
    }
  };
  const save = (prefs: Record<string, boolean>) => {
    localStorage.setItem("fsl_notif_prefs", JSON.stringify(prefs));
  };

  const defaults = {
    progressReports: true,
    showcaseReminders: true,
    paymentReminders: true,
    messages: true,
  };
  const [prefs, setPrefs] = useState<Record<string, boolean>>({
    ...defaults,
    ...load(),
  });

  const toggle = (key: string) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    save(next);
  };

  const items = [
    {
      key: "progressReports",
      label: "Progress reports",
      desc: "Email when a new weekly report is published for your child",
    },
    {
      key: "showcaseReminders",
      label: "Showcase reminders",
      desc: "Email 1 week and 1 day before a showcase event",
    },
    {
      key: "paymentReminders",
      label: "Payment reminders",
      desc: "Email when a payment is due or overdue",
    },
    {
      key: "messages",
      label: "New messages",
      desc: "Email when the academy sends you a reply",
    },
  ];

  return (
    <SectionCard
      title="Notification preferences"
      subtitle="Choose what emails you receive from Future Smart Lab. Changes are saved immediately."
    >
      <div className="space-y-3">
        {items.map((item: { key: string; label: string; desc: string }) => (
          <div
            key={item.key}
            className="flex items-center justify-between gap-4 rounded-[16px] border border-[#ebeff7] bg-[#f9fafc] px-4 py-3"
          >
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-[#16213e]">{item.label}</p>
              <p className="text-[11.5px] text-[#8189a0] mt-0.5 leading-4">
                {item.desc}
              </p>
            </div>
            <button
              onClick={() => toggle(item.key)}
              className={[
                "relative h-5 w-9 flex-shrink-0 rounded-full transition-colors",
                prefs[item.key] ? "bg-[#1b2d5e]" : "bg-[#dde2ec]",
              ].join(" ")}
              aria-checked={prefs[item.key]}
              role="switch"
            >
              <span
                className={[
                  "absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform",
                  prefs[item.key] ? "translate-x-4" : "translate-x-0.5",
                ].join(" ")}
              />
            </button>
          </div>
        ))}
      </div>
      <p className="text-[11.5px] text-[#a6adbf]">
        These preferences are stored locally. Backend sync coming soon.
      </p>
    </SectionCard>
  );
}

/* ─── account info ───────────────────────────────────────────────── */

function AccountInfoSection({ profile }: { profile: ParentProfile }) {
  const joined = new Date(profile.createdAt).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <SectionCard title="Account information">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {[
          { label: "Member since", value: joined },
          { label: "Children", value: String(profile.children.length) },
          {
            label: "Last login",
            value: profile.lastLoginAt
              ? new Date(profile.lastLoginAt).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })
              : "—",
          },
        ].map((item: { label: string; value: string }) => (
          <div
            key={item.label}
            className="rounded-[14px] bg-[#f1f3f8] px-3 py-2.5"
          >
            <p className="text-[11px] font-bold uppercase tracking-wider text-[#8189a0]">
              {item.label}
            </p>
            <p className="mt-0.5 text-sm font-bold text-[#16213e]">
              {item.value}
            </p>
          </div>
        ))}
      </div>

      <div className="rounded-[16px] border border-[#e5e8ef] bg-[#f9fafc] px-4 py-4 text-xs text-[#5a6278] leading-6">
        <p className="font-semibold text-[#16213e] mb-1">Need help?</p>
        <p>
          Contact the academy team through the Messages tab for account-related
          queries. For urgent matters call us directly.
        </p>
      </div>
    </SectionCard>
  );
}

/* ─── main page ──────────────────────────────────────────────────── */

export default function UserSettings() {
  const user = useSelector(selectUser);
  const isParent = user?.role === "parent";
  // The profile endpoint is the parent's; students only change their password here.
  const { data, isLoading, isError } = useGetProfileQuery(undefined, { skip: !isParent });
  const profile: ParentProfile | undefined = data?.data;
  useHashScroll(!isParent || !!profile);

  if (!isParent)
    return (
      <div className="max-w-2xl space-y-6">
        <div>
          <h1 className="text-xl font-bold text-[#16213e]">Settings</h1>
          <p className="mt-1 text-sm text-[#8189a0]">Your account and password.</p>
        </div>
        <SectionCard title="Your account">
          <div className="grid gap-2 sm:grid-cols-2">
            {[
              { label: "Name", value: `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim() || "—" },
              { label: "Email", value: user?.email ?? "—" },
            ].map((item) => (
              <div key={item.label} className="rounded-[14px] bg-[#f1f3f8] px-3 py-2.5">
                <p className="text-[11px] font-bold uppercase tracking-wider text-[#8189a0]">{item.label}</p>
                <p className="mt-0.5 break-all text-sm font-bold text-[#16213e]">{item.value}</p>
              </div>
            ))}
          </div>
          <p className="text-xs leading-5 text-[#5f6780]">
            To change your name or email, ask your parent to message the academy team.
          </p>
        </SectionCard>
        <div id="password" className="scroll-mt-24">
          <ChangePasswordSection />
        </div>
      </div>
    );

  if (isLoading)
    return (
      <div className="space-y-5 max-w-2xl">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-72" />
        </div>
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-48 rounded-[22px]" />
        ))}
      </div>
    );

  if (isError || !profile)
    return (
      <div className="rounded-[18px] border border-[#f5c0c0] bg-[#fff3f3] px-5 py-4 text-sm text-[#9b2a2a]">
        Failed to load your profile. Please refresh the page.
      </div>
    );

  return (
    <div className="max-w-2xl space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-xl font-bold text-[#16213e]">Settings</h1>
        <p className="mt-1 text-sm text-[#8189a0]">
          Manage your account, password, and notification preferences.
        </p>
      </div>

      <div id="profile" className="scroll-mt-24">
        <ProfileSection profile={profile} />
      </div>
      <div id="password" className="scroll-mt-24">
        <ChangePasswordSection />
      </div>
      <ChildrenSection profile={profile} />
      <NotificationSection />
      <AccountInfoSection profile={profile} />
    </div>
  );
}
